// "Contacts on this order" (Stephanie 2026-10-12): tick which of the client's
// people are on this order. Required before a proof can go to the client —
// "Send to client" lists only these. Hidden until Jake runs
// scripts/push-order-contacts.ts.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";

interface Row { id: string; name: string; email: string | null; main: boolean; onOrder: boolean }

export function OrderContactsCard({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const key = ["projects", projectId, "order-contacts"];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ available: boolean; contacts: Row[] }>(`/projects/${projectId}/order-contacts`) });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (q.data) setPicked(new Set(q.data.contacts.filter((c) => c.onOrder).map((c) => c.id)));
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api(`/projects/${projectId}/order-contacts`, { method: "PUT", body: JSON.stringify({ contact_ids: [...picked] }) }),
    onSuccess: () => { setError(null); setSaved(true); void qc.invalidateQueries({ queryKey: key }); },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(b?.error ?? "We couldn't save that. Try again.");
    },
  });
  if (!q.data?.available) return null;
  const current = new Set(q.data.contacts.filter((c) => c.onOrder).map((c) => c.id));
  const changed = picked.size !== current.size || [...picked].some((id) => !current.has(id));
  return (
    <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]" aria-labelledby="order-contacts">
      <h2 id="order-contacts" className="text-lg font-semibold tracking-[-0.4px] text-ink">Contacts on this order</h2>
      <p className="mt-1 text-sm text-muted">Required before a proof can be sent to the client. Proofs go only to the people ticked here.</p>
      {current.size === 0 && <p className="mt-3 rounded-md bg-warn-bg px-3 py-2 text-sm text-warn">No contacts on this order yet.</p>}
      {q.data.contacts.length === 0 ? (
        <p className="mt-3 text-sm text-muted">This client has no contacts yet. Add one on the client's Contacts tab.</p>
      ) : (
        <div className="mt-3 space-y-1">
          {q.data.contacts.map((c) => (
            <label key={c.id} className="flex min-h-[40px] items-center gap-3 text-[15px]">
              <input
                type="checkbox"
                className="h-4 w-4 accent-ink"
                checked={picked.has(c.id)}
                onChange={() => {
                  const next = new Set(picked);
                  if (next.has(c.id)) next.delete(c.id);
                  else next.add(c.id);
                  setPicked(next);
                  setSaved(false);
                }}
              />
              <span>
                {c.name}
                {c.main && <span className="ml-2 text-xs text-muted">main contact</span>}
                <span className="block text-[13px] text-muted">{c.email ?? "no email on file"}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-risk">{error}</p>}
      <div className="mt-3 flex items-center justify-end gap-3">
        {saved && !changed && <span className="text-sm text-muted">Saved</span>}
        <Button size="sm" onClick={() => save.mutate()} disabled={!changed || save.isPending}>{save.isPending ? "Saving…" : "Save contacts"}</Button>
      </div>
    </section>
  );
}
